import { number, t } from "./_i18n.js";
import { toolError, toolUsed } from "./_tool_event.js";

const TOOL = "domain-expiry-checker";
const form = document.getElementById("domain-form");
const input = document.getElementById("domain-name");
const button = document.getElementById("domain-submit");
const out = document.getElementById("domain-result");
let busy = false;

if (form && input && button && out) {
    form.addEventListener("submit", (event) => {
        event.preventDefault();
        run();
    });
}

function el(tag, cls, text) {
    const node = document.createElement(tag);
    node.className = cls;
    if (text !== undefined) node.textContent = text;
    return node;
}

// `lang` marks a sentence that is not in the page's language: the probe
// writes its own in English.
function message(text, lang) {
    const node = el("p", "tool-dns__empty mk-mono", text);
    if (lang) node.lang = lang;
    out.replaceChildren(node);
}

async function run() {
    if (busy) return;
    const domain = input.value.trim();
    if (!domain || domain.length > 2048) {
        message(t("expiry-js-empty"));
        input.focus();
        return;
    }
    busy = true;
    button.disabled = true;
    button.textContent = t("expiry-js-checking");
    form.setAttribute("aria-busy", "true");
    message(t("expiry-js-reading"));
    // Never send the submitted domain, URL, or registry response to analytics.
    toolUsed(TOOL);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    try {
        const res = await fetch(`${form.dataset.probe}?domain=${encodeURIComponent(domain)}`, {
            headers: { accept: "application/json" }, signal: controller.signal,
        });
        let body;
        try { body = await res.json(); } catch { body = null; }
        if (!res.ok || body?.ok !== true) {
            if (typeof body?.error === "string") message(body.error, "en");
            else message(t("expiry-js-unreadable"));
            toolError(TOOL, { reason: `status-${res.status}` });
            return;
        }
        if (!validReport(body)) {
            message(t("expiry-js-malformed"));
            toolError(TOOL, { reason: "malformed-body" });
            return;
        }
        out.replaceChildren(render(body));
    } catch (error) {
        message(t(error.name === "AbortError" ? "expiry-js-timeout" : "expiry-js-unreachable"));
        toolError(TOOL, { reason: error.name === "AbortError" ? "timeout" : "probe-unreachable" });
    } finally {
        clearTimeout(timer);
        busy = false;
        button.disabled = false;
        button.textContent = t("expiry-js-check");
        form.setAttribute("aria-busy", "false");
    }
}

function validReport(r) {
    return typeof r.domain === "string" && r.domain.length > 0
        && Number.isInteger(r.days_remaining) && typeof r.expired === "boolean"
        && typeof r.expires_at === "string" && Number.isFinite(Date.parse(r.expires_at))
        && typeof r.checked_at === "string" && Number.isFinite(Date.parse(r.checked_at))
        && (r.registrar === null || typeof r.registrar === "string");
}

function timestamp(iso) {
    return new Date(iso).toISOString().replace("T", " ").replace(/\.\d{3}Z$/, " UTC");
}

function verdict(r) {
    const count = Math.abs(r.days_remaining);
    const shown = number(count);
    if (r.expired) return count === 0 ? t("expiry-js-passed") : t("expiry-js-passed-ago", { count, shown });
    return count === 0 ? t("expiry-js-under-a-day") : t("expiry-js-days-left", { count, shown });
}

function render(r) {
    const fragment = document.createDocumentFragment();
    fragment.append(el("p", "tool-dns__head mk-mono", t("expiry-js-registration-for", { domain: r.domain })));
    const tone = r.expired ? "dead" : r.days_remaining <= 7 ? "critical" : r.days_remaining <= 30 ? "warn" : "ok";
    const status = el("p", `tool-ssl__verdict mk-mono tool-ssl__verdict--${tone}`);
    status.append(el("span", "tool-ssl__days", verdict(r)));
    status.append(el("span", "tool-ssl__until", t("expiry-js-registry-expiry", { time: timestamp(r.expires_at) })));
    fragment.append(status);

    const facts = el("dl", "tool-ssl__facts");
    const rows = [
        [t("expiry-js-registered-domain"), r.domain],
        [t("expiry-js-registrar"), r.registrar || t("expiry-js-not-provided")],
        [t("expiry-js-checked-at"), timestamp(r.checked_at)],
    ];
    for (const [label, value] of rows) {
        facts.append(el("dt", "tool-metric__label", label));
        facts.append(el("dd", "tool-ssl__value mk-mono", value));
    }
    fragment.append(facts);
    try {
        const source = new URL(r.source_url);
        if (source.protocol === "https:" && !source.username && !source.password) {
            const link = el("a", "mk-link", t("expiry-js-view-response"));
            link.href = source.href;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            fragment.append(link);
        }
    } catch { /* A malformed source link must not hide the date. */ }
    fragment.append(el("p", "tool-dns__warn mk-mono", t(r.expired ? "expiry-js-warn-passed" : "expiry-js-warn-confirm")));
    const cta = el("a", "mk-cta mk-cta--primary tool-dns__cta", t("expiry-js-monitor"));
    cta.href = `/start?kind=domain_expiry&url=${encodeURIComponent(r.domain)}`;
    cta.dataset.umamiEvent = "signup-start";
    cta.dataset.umamiEventPosition = "tool-domain-result";
    fragment.append(cta);
    return fragment;
}
